import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Divider,
    InputAdornment,
    Container,
    Stack,
    Avatar,
    Dialog,
    DialogContent,
    IconButton
} from '@mui/material';
import {
    Search as SearchIcon,
    LocalShipping as ShippingIcon,
    LocationOn as LocationIcon,
    Schedule as TimeIcon,
    CheckCircle as CheckIcon,
    RadioButtonUnchecked as PendingIcon,
    TrendingFlat as ArrowIcon,
    Info as InfoIcon,
    Circle as CircleIcon,
    VerifiedUser as SealIcon,
    Image as ImageIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import { trackingService } from '../../services/trackingService';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { useLocation } from 'react-router-dom';

const TrackShipment = () => {
    const { handleApiCallWithLoading, showError, showInfo, showWarning } = useApiHandler();
    const location = useLocation();

    const [awbNumber, setAwbNumber] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const handleTrackShipmentWithAwb = async (awb) => {
        if (!awb || !awb.trim()) {
            showWarning('Please enter AWB number');
            return;
        }

        setLoading(true);
        setSearched(true);

        try {
            const response = await trackingService.trackShipment(awb.trim());
            if (response.success) {
                setTrackingData(response.data);
            } else {
                setTrackingData(null);
                showError(response.message || 'Failed to track shipment');
            }
        } catch (error) {
            console.error('Error tracking shipment:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                showError('Failed to track shipment. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTrackShipment = async () => {
        await handleTrackShipmentWithAwb(awbNumber);
    };

    // Check for AWB number in navigation state and auto-search
    useEffect(() => {
        if (location.state?.awbNumber) {
            setAwbNumber(location.state.awbNumber);
            // Automatically trigger the search
            handleTrackShipmentWithAwb(location.state.awbNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleTrackShipment();
        }
    };

    // Handle image viewing
    const handleImageView = (imageUrl, altText = 'Delivery receipt') => {
        setSelectedImage({ url: imageUrl, alt: altText });
        setImageViewerOpen(true);
    };

    const handleCloseImageViewer = () => {
        setImageViewerOpen(false);
        setSelectedImage(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
                return 'success';
            case 'out for delivery':
                return 'warning';
            case 'in transit':
                return 'info';
            case 'picked up':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getTimelineDotColor = (activity) => {
        const activityLower = activity.toLowerCase();
        if (activityLower.includes('delivered')) return 'success';
        if (activityLower.includes('out for delivery')) return 'warning';
        if (activityLower.includes('picked') || activityLower.includes('collected')) return 'primary';
        if (activityLower.includes('arrived') || activityLower.includes('reached')) return 'info';
        return 'grey';
    };

    const getProgressPercentage = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
                return 100;
            case 'out for delivery':
                return 80;
            case 'arrived at destination':
                return 70;
            case 'in transit':
                return 50;
            case 'picked up':
                return 30;
            default:
                return 10;
        }
    };

    return (
        <DashboardLayout title="Track Shipment" userType="customer">
            <Container maxWidth="lg">
                <Box sx={{ py: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <ShippingIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h3" fontWeight="bold" gutterBottom>
                            Track Your Shipment
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Enter your AWB number to track your shipment status
                        </Typography>
                    </Box>

                {/* Search Section */}
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="AWB Number"
                                placeholder="Enter your AWB number (e.g., SX24123456)"
                                value={awbNumber}
                                onChange={(e) => setAwbNumber(e.target.value)}
                                onKeyPress={handleKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        height: '56px'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleTrackShipment}
                                disabled={loading}
                                sx={{ height: '56px' }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Track Shipment'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            Searching for your shipment...
                        </Typography>
                    </Box>
                )}

                {/* No Results */}
                {searched && !loading && !trackingData && (
                    <Alert severity="error" sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Shipment Not Found
                        </Typography>
                        <Typography variant="body2">
                            We couldn't find a shipment with AWB number "{awbNumber}". 
                            Please check the number and try again.
                        </Typography>
                    </Alert>
                )}

                {/* Tracking Results */}
                {trackingData && !loading && (
                    <>
                        {/* Shipment Overview */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={8}>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            AWB: {trackingData.booking_info.awb_no}
                                        </Typography>
                                        
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">From:</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {trackingData.booking_info.from_city}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {trackingData.booking_info.shipper.name}
                                                    {trackingData.booking_info.shipper.company && 
                                                        ` - ${trackingData.booking_info.shipper.company}`}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">To:</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {trackingData.booking_info.to_city}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {trackingData.booking_info.recipient.name}
                                                    {trackingData.booking_info.recipient.company && 
                                                        ` - ${trackingData.booking_info.recipient.company}`}
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">Weight:</Typography>
                                                <Typography variant="body1">
                                                    {trackingData.booking_info.weight} kg
                                                </Typography>
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">Packages:</Typography>
                                                <Typography variant="body1">
                                                    {trackingData.booking_info.packages} piece(s)
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Current Status
                                        </Typography>
                                        <Chip 
                                            label={trackingData.booking_info.status}
                                            color={getStatusColor(trackingData.booking_info.status)}
                                            size="large"
                                            sx={{ 
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                height: '40px',
                                                mb: 2
                                            }}
                                        />
                                        
                                        {/* Progress Bar */}
                                        <Box sx={{ width: '100%', mt: 2 }}>
                                            <Box sx={{ 
                                                width: '100%', 
                                                height: 8, 
                                                bgcolor: 'grey.300', 
                                                borderRadius: 1,
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}>
                                                <Box sx={{
                                                    width: `${getProgressPercentage(trackingData.booking_info.status)}%`,
                                                    height: '100%',
                                                    bgcolor: `${getStatusColor(trackingData.booking_info.status)}.main`,
                                                    borderRadius: 1,
                                                    transition: 'width 0.5s ease-in-out'
                                                }} />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                {getProgressPercentage(trackingData.booking_info.status)}% Complete
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Tracking Timeline */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <TimeIcon sx={{ mr: 1 }} />
                                Tracking History
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            {trackingData.tracking_history.length === 0 ? (
                                <Alert severity="info" icon={<InfoIcon />}>
                                    <Typography variant="body1">
                                        No tracking updates available yet.
                                    </Typography>
                                </Alert>
                            ) : (
                                <Box>
                                    {trackingData.tracking_history.map((entry, index) => (
                                        <Box key={entry.id} sx={{ display: 'flex', mb: 3 }}>
                                            {/* Timeline dot and connector */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: `${getTimelineDotColor(entry.activity)}.main`,
                                                        width: 40,
                                                        height: 40,
                                                        border: index === 0 ? '3px solid' : 'none',
                                                        borderColor: index === 0 ? `${getTimelineDotColor(entry.activity)}.dark` : 'none'
                                                    }}
                                                >
                                                    {getTimelineDotColor(entry.activity) === 'success' ? (
                                                        <CheckIcon />
                                                    ) : (
                                                        <CircleIcon sx={{ fontSize: 12 }} />
                                                    )}
                                                </Avatar>
                                                {index < trackingData.tracking_history.length - 1 && (
                                                    <Box
                                                        sx={{
                                                            width: 2,
                                                            height: 60,
                                                            bgcolor: 'grey.300',
                                                            mt: 1
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Content */}
                                            <Box sx={{ flex: 1 }}>
                                                {/* Date and time */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mr: 2 }}>
                                                        {entry.date}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.time}
                                                    </Typography>
                                                </Box>

                                                {/* Activity card */}
                                                <Card 
                                                    variant="outlined" 
                                                    sx={{ 
                                                        p: 2,
                                                        bgcolor: index === 0 ? 'action.hover' : 'background.paper'
                                                    }}
                                                >
                                                    <Typography variant="h6" component="div" fontWeight="bold">
                                                        {entry.activity}
                                                    </Typography>
                                                    
                                                    <Typography 
                                                        variant="body2" 
                                                        color="text.secondary" 
                                                        sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            mt: 1 
                                                        }}
                                                    >
                                                        <LocationIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                        {entry.location}
                                                    </Typography>
                                                    
                                                    {entry.remark && (
                                                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                            "{entry.remark}"
                                                        </Typography>
                                                    )}

                                                    {/* Delivery Confirmation Info for customers */}
                                                    {entry.is_sealed_signed !== undefined && (
                                                        <Box sx={{ 
                                                            mt: 2, 
                                                            p: 2, 
                                                            bgcolor: entry.is_sealed_signed ? 'success.50' : 'warning.50',
                                                            borderRadius: 1,
                                                            border: '1px solid',
                                                            borderColor: entry.is_sealed_signed ? 'success.200' : 'warning.200'
                                                        }}>
                                                            <Typography 
                                                                variant="subtitle2" 
                                                                sx={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center',
                                                                    color: entry.is_sealed_signed ? 'success.main' : 'warning.main',
                                                                    mb: 1,
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                <SealIcon sx={{ mr: 1, fontSize: 16 }} />
                                                                Delivery Verification
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {entry.is_sealed_signed 
                                                                    ? "✓ Your package was delivered with proper seal and signature verification" 
                                                                    : "Package delivered (verification details not available)"}
                                                            </Typography>

                                                            {/* Show signed copy to customer if available */}
                                                            {entry.signed_copy_url && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                                                        <ImageIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                                                        Delivery Receipt:
                                                                    </Typography>
                                                                    <Card 
                                                                        elevation={1} 
                                                                        sx={{ 
                                                                            p: 1, 
                                                                            display: 'inline-block',
                                                                            cursor: 'pointer',
                                                                            maxWidth: '200px',
                                                                            position: 'relative',
                                                                            '&:hover': {
                                                                                elevation: 3,
                                                                                '& .zoom-overlay': {
                                                                                    opacity: 1
                                                                                }
                                                                            }
                                                                        }}
                                                                        onClick={() => handleImageView(entry.signed_copy_url, 'Delivery receipt')}
                                                                    >
                                                                        <Box sx={{ position: 'relative' }}>
                                                                            <img
                                                                                src={entry.signed_copy_url}
                                                                                alt="Delivery receipt"
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: 'auto',
                                                                                    maxHeight: '120px',
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '4px'
                                                                                }}
                                                                            />
                                                                            <Box
                                                                                className="zoom-overlay"
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    bottom: 0,
                                                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    opacity: 0,
                                                                                    transition: 'opacity 0.2s ease',
                                                                                    borderRadius: '4px'
                                                                                }}
                                                                            >
                                                                                <ZoomInIcon sx={{ color: 'white', fontSize: 24 }} />
                                                                            </Box>
                                                                        </Box>
                                                                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                                                                            View Receipt
                                                                        </Typography>
                                                                    </Card>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Card>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    </>
                )}

                {/* Image Viewer Modal */}
                <Dialog
                    open={imageViewerOpen}
                    onClose={handleCloseImageViewer}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            overflow: 'visible'
                        }
                    }}
                    BackdropProps={{
                        sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            backdropFilter: 'blur(4px)'
                        }
                    }}
                >
                    <DialogContent sx={{ p: 0, position: 'relative', textAlign: 'center' }}>
                        {selectedImage && (
                            <>
                                <IconButton
                                    onClick={handleCloseImageViewer}
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        color: 'text.primary',
                                        zIndex: 1,
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 1)'
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <img
                                    src={selectedImage.url}
                                    alt={selectedImage.alt}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '90vh',
                                        width: 'auto',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                                    }}
                                />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'white', 
                                        mt: 2, 
                                        textAlign: 'center',
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 1,
                                        display: 'inline-block'
                                    }}
                                >
                                    {selectedImage.alt}
                                </Typography>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </Container>
        </DashboardLayout>
    );
};

export default TrackShipment; 